import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ApplyColumnStateParams, ColDef, ColumnApi, CsvExportParams, GridApi, GridReadyEvent, IsFullWidthRowParams, ProcessCellForExportParams } from 'ag-grid-community';
import isEmpty from 'lodash-es/isEmpty';
import { BehaviorSubject, debounceTime, delay, distinctUntilChanged, Observable, Subscription, tap } from 'rxjs';
import { INVOICE_MAX_SELECTION_ALLOWED } from '../../constants/default.const';
import { SubComponentsToLoad } from '../../enums/components-to-load.enum';
import { DialogContentType, DialogType } from '../../enums/dialog-type.enum';
import { ConsumerPortalConfig } from '../../models/consumer-portal-config';
import { ContractCP, GetDocumentParam } from '../../models/contract-model';
import { Invoice } from '../../models/invoice-model';
import { FormatDatePipe } from '../../pipes/format-date.pipe';
import { AppFacadeService } from '../../services/app-facade.service';
import { AppMediatorService } from '../../services/app-mediator.service';
import { ConfigurationService } from '../../services/configuration.service';
import { FeatureToggleService } from '../../services/feature-toggle.service';
import { InvoicesService } from '../../services/invoices.service';
import { LoggerService } from '../../services/logger.service';
import { NotificationService } from '../../services/notification.service';
import { Filter, FilterOutput } from '../../shared/models/filters.model';
import { FormatAddressPipe } from '../../shared/pipes/format-address.pipe';
import { clearInvoicesinStore, increasePageNumberInvoices } from '../../store/domains/invoices/invoices.actions';
import { getInvoices, getInvoiceTotalDue, getNoMoreRecordInvoices } from '../../store/domains/invoices/invoices.selectors';
import { Address, formatDateType } from '../../types';
import { InvoicesFullWidthRowComponent } from '../invoices-full-width-row/invoices-full-width-row.component';
import { AmountRendererComponent } from './renderer/amount/amount-renderer.component';
import { InvoiceNumberCellRendererComponent } from './renderer/invoice-number/invoice-number-cell-renderer.component';
import { InvoiceSelectionCheckboxRendererComponent } from './renderer/invoice-selection-checkbox-renderer.component';

@Component({
    standalone: false,
    selector: 'por-invoices',
    templateUrl: './invoices.component.html',
    styleUrls: ['./invoices.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvoicesComponent implements OnInit, OnDestroy {
    readonly appFacadeService = inject(AppFacadeService);
    private readonly store = inject(Store);
    private readonly invoiceService = inject(InvoicesService);
    private readonly notification = inject(NotificationService);
    private readonly configService = inject(ConfigurationService);
    private readonly datePipe = inject(FormatDatePipe);
    private readonly translateService = inject(TranslateService);
    private readonly appMediatorService = inject(AppMediatorService);
    private readonly logger = inject(LoggerService);
    private readonly breakpointObserver = inject(BreakpointObserver);
    readonly featureToggleService = inject(FeatureToggleService);
    private readonly formatAddressPipe = inject(FormatAddressPipe);

    currentWidth = window.innerWidth;
    public context;
    invoiceTotalAmountDue$!: Observable<number | undefined>;
    invoices$: Observable<Invoice[]> = this.store.select(getInvoices);
    noMoreRecord$: Observable<boolean> = this.store.select(getNoMoreRecordInvoices);
    customerId!: string;
    private readonly subscriptions: Subscription[] = [];
    gridApi!: GridApi;
    gridColumnApi!: ColumnApi;
    customerStatus?: string;
    private paymentProcessorsRequested = false;
    headerHeight = 35;
    rowHeight = 65;

    inputs: ConsumerPortalConfig | undefined;
    gridReady$: BehaviorSubject<boolean> = new BehaviorSubject(true);
    gridReadyObservable$: Observable<boolean> = this.gridReady$.pipe(delay(1000));

    @Input() config: string | undefined;
    @Input() width: string | undefined;
    @Input() height: string | undefined;
    @Output() readonly elementLoaded = new EventEmitter<boolean>();
    @Output() readonly appOutput: EventEmitter<ContractCP[] | Invoice[]> = new EventEmitter<ContractCP[] | Invoice[]>();

    uiUrl: string | undefined;
    @Input() initialStatusFilters?: string[];
    filters: FilterOutput | undefined;
    singleFilters: Filter[] = [
        { label: this.translateService.instant('InvoiceStatusAll'), value: 'All' },
        { label: this.translateService.instant('InvoiceStatusUnpaid'), value: 'Unpaid' },
        { label: this.translateService.instant('InvoiceStatusPaid'), value: 'Paid' }
    ];
    // 'Unpaid' default applies only where the Paid/Unpaid/All toggle is visible (canSeeInvoiceStatusFilter,
    // expert/Elite). Orgs with the toggle hidden keep the 'All' default so the full list (incl. credits)
    // shows with no control to change it — see store/domains/invoices/CLAUDE.md.
    get singleFiltersDefaultValue(): string {
        return this.featureToggleService.isAvailable('canSeeInvoiceStatusFilter') ? 'Unpaid' : 'All';
    }
    statusFilter: string = this.singleFiltersDefaultValue;

    // canSeeInvoiceStatusFilter (expert only) gates the Paid/Unpaid/All toggle. When disabled, pass an
    // empty list so por-filters hides the toggle on desktop and the radio group on mobile; the list then
    // uses the org-aware singleFiltersDefaultValue.
    get statusFilterOptions(): Filter[] {
        return this.featureToggleService.isAvailable('canSeeInvoiceStatusFilter') ? this.singleFilters : [];
    }

    dialogType: DialogType = DialogType.information;
    dialogContentType = DialogContentType;

    payInvoicesClicked = signal(false);
    public rowSelection: 'single' | 'multiple' = 'multiple';
    selectedInvoices: string[] = [];
    overlayStates = false;

    constructor() {
        const store = this.store;

        store.dispatch(clearInvoicesinStore());
        this.context = {
            componentParent: this
        };
    }

    public filterComponentProps: { searchPlaceHolder: string; dateLabel: string } = {
        searchPlaceHolder: this.translateService.instant('QuickSearch'),
        dateLabel: this.translateService.instant('SelectInvoiceDateRange')
    };

    public columnDefs: ColDef[] = [];
    public defaultColDef: ColDef = {
        sortable: true,
        resizable: true,
        suppressMovable: true,
        wrapText: false,
        sortingOrder: ['asc', 'desc'],
        suppressMenu: true
    };
    public fullWidthCellRenderer = InvoicesFullWidthRowComponent;

    public getScreenWidth: BehaviorSubject<number> = new BehaviorSubject(window.innerWidth);

    isFullWidth = false;

    public isFullWidthRow: (params: IsFullWidthRowParams) => boolean = () => {
        return this.isFullWidth;
    };

    //Need to check if it exceeds the breakpoint, then need to check responsive grid
    @HostListener('window:resize', ['$event'])
    onWindowResize() {
        if (this.currentWidth >= 1024 && window.innerWidth <= 1024) {
            this.getScreenWidth.next(window.innerWidth);
        } else if (this.currentWidth <= 1024 && window.innerWidth >= 1024) {
            this.getScreenWidth.next(window.innerWidth);
        }
        this.currentWidth = window.innerWidth;
    }

    private readonly formatDate = (params: formatDateType) => {
        const date = this.datePipe.transform(params.value, this.appFacadeService.featureToggleService.isSyrinx());
        return `<span class="theme-table-row">${date}</span>`;
    };

    onGridReady(params: GridReadyEvent) {
        this.gridApi = params.api;
        this.gridColumnApi = params.columnApi;
        this.gridApi.setDomLayout('autoHeight');
        const columnState: ApplyColumnStateParams = {
            state: [
                {
                    colId: 'InvoiceDate',
                    sort: 'desc'
                }
            ]
        };
        this.gridColumnApi.applyColumnState(columnState);
        this.gridApi.sizeColumnsToFit();
    }
    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    ngOnInit(): void {
        const lang = this.appMediatorService.localStorageService.selectedContentLanguage;
        if (lang && lang !== this.translateService.getCurrentLang()) {
            this.translateService.use(lang);
        }

        this.subscriptions.push(
            this.getScreenWidth.pipe(debounceTime(1000)).subscribe((width: number) => {
                this.setResponsiveGrid(width);
            })
        );
        this.subscriptions.push(
            this.appFacadeService.getSelectedInvoices().subscribe((selectedInvoices: string[]) => {
                this.selectedInvoices = selectedInvoices;
            })
        );
        this.subscriptions.push(
            this.appFacadeService.getCustomer().subscribe(customer => {
                this.customerStatus = customer?.Status;
                // The customer can arrive after ngOnInit (Syrinx cash orgs); isMultiPaymentAvailable()
                // only becomes true once customerStatus is set, so (re)attempt the processor load here.
                this.ensurePaymentProcessorsLoaded();
                if (this.gridApi && this.gridColumnApi) {
                    this.setResponsiveGrid(this.currentWidth);
                }
            })
        );
        this.configService.setMatIconFontFamily();
        if (this.config && !isEmpty(this.config)) {
            this.inputs = JSON.parse(this.config);
            this.logger.logAssert(this.inputs?.customerId, 'customerId is not defined');
            this.customerId = this.inputs?.customerId || '0';
        }
        if (!this.customerId) {
            this.notification.error(this.translateService.instant('customerIdNotConfigured'));
            this.elementLoaded.emit(false);
            return;
        }

        this.elementLoaded.emit(true);
        this.subscriptions.push(
            this.invoices$.subscribe(invoices => {
                if (!isEmpty(invoices)) {
                    this.appOutput.emit(invoices);
                }
                const hasContractId = (invoices ?? []).some(i => !!i?.Identifiers?.ContractId);
                this.gridColumnApi?.setColumnVisible('Identifiers.ContractId', hasContractId);
            })
        );

        // checking window size
        this.subscriptions.push(
            this.breakpointObserver
                .observe(['(max-width: 992px)'])
                .pipe(
                    tap((value: BreakpointState) => {
                        if (value.matches) {
                            this.filterComponentProps = {
                                searchPlaceHolder: this.translateService.instant('SearchInvoices'),
                                dateLabel: this.translateService.instant('filterbyDate')
                            };
                        } else {
                            this.filterComponentProps = {
                                searchPlaceHolder: this.translateService.instant('QuickSearch'),
                                dateLabel: this.translateService.instant('SelectInvoiceDateRange')
                            };
                        }
                    }),
                    distinctUntilChanged()
                )
                .subscribe()
        );
        // ADO-116163: source the "Total Due" header from the store so a successful invoice payment
        // can refresh it (via fetchInvoiceTotalDue in payment.effects), then kick the initial fetch.
        this.invoiceTotalAmountDue$ = this.store.select(getInvoiceTotalDue);
        this.invoiceService.getInvoiceTotalDue(this.customerId);
        this.ensurePaymentProcessorsLoaded();
        this.uiUrl = this.inputs?.uiUrl;
    }

    filter($e: FilterOutput) {
        this.filters = $e;
        // FilterOutput.singleFilter is the single source of truth for the status toggle, so the
        // status can't drift from the emitted filters (e.g. mobile "Clear all", or a toggle during
        // an in-flight fetch). loadMore() reuses this persisted value.
        this.statusFilter = $e.singleFilter || this.singleFiltersDefaultValue;
        this.invoiceService.filter($e, this.customerId, this.statusFilter);
    }

    loadMore() {
        if (!this.customerId) {
            this.notification.error(this.translateService.instant('customerIdNotConfigured'));
            return;
        }

        this.store.dispatch(increasePageNumberInvoices());
        this.invoiceService.getInvoices(this.customerId, this.filters?.dates?.startDate, this.filters?.dates?.endDate, this.filters?.search, this.statusFilter);
        this.gridApi.refreshCells();
    }

    exportCSV() {
        const visibleColumnKeys = this.gridColumnApi
            .getAllDisplayedColumns()
            .map(column => column.getColId())
            .filter(colId => colId !== 'selection');
        const params: CsvExportParams = {
            skipHeader: false,
            skipGroups: true,
            fileName: 'Invoices.csv',
            columnKeys: visibleColumnKeys,
            processCellCallback: (param: ProcessCellForExportParams) => {
                return this.processCellCSV(param);
            }
        };
        this.gridApi.exportDataAsCsv(params);
    }

    processCellCSV(params: ProcessCellForExportParams): string {
        const colId = params.column.getColId();
        if (colId && ['InvoiceDate', 'DueDate'].includes(colId)) {
            return this.datePipe.transform(params.value, this.appFacadeService.featureToggleService.isSyrinx());
        }
        if (colId && ['AdditionalFields.Address'].includes(colId)) {
            return this.formatAddressPipe.format(params?.value);
        }
        return params.value;
    }

    onSelectionChanged(isSelected: boolean, invoiceId: string): void {
        this.addRemoveInvoiceFromState(isSelected, invoiceId);
        if (this.selectedInvoices.length === 0) {
            this.toggleOverlay(false);
        } else if (this.selectedInvoices.length > 0) {
            this.toggleOverlay(true);
        }
    }

    onFullWidthRowSelectionChanged(isSelected: boolean, invoiceId: string): void {
        this.addRemoveInvoiceFromState(isSelected, invoiceId);
    }

    private addRemoveInvoiceFromState(isSelected: boolean, invoiceId: string): void {
        if (isSelected) {
            if (this.selectedInvoices.length >= INVOICE_MAX_SELECTION_ALLOWED) {
                this.notification.error(
                    this.translateService.instant('MaximumInvoicesExceeded', {
                        max: INVOICE_MAX_SELECTION_ALLOWED,
                        provided: this.selectedInvoices.length + 1
                    })
                );
                return;
            }
            this.selectedInvoices = [...this.selectedInvoices, invoiceId];
            this.appFacadeService.setSelectedInvoices(this.selectedInvoices);
        } else {
            this.appFacadeService.unsetSelectedInvoices([invoiceId]);
        }
    }

    // Load payment processors at most once, as soon as multipay is available and the customer id is
    // known. Guards the init path and the late-arriving getCustomer() subscription against a double fetch.
    private ensurePaymentProcessorsLoaded(): void {
        if (this.paymentProcessorsRequested || !this.customerId || !this.isMultiPaymentAvailable()) {
            return;
        }
        this.paymentProcessorsRequested = true;
        this.appFacadeService.getPaymentProcessors(this.customerId);
    }

    public isMultiPaymentAvailable(): boolean {
        const base =
            this.featureToggleService.isAvailable('invoiceMultiPay') && this.featureToggleService.isAvailable('Account.SeePricing') && this.featureToggleService.isAvailable('capturePaymentFeature');
        if (!base) {
            return false;
        }
        if (this.featureToggleService.isSyrinx()) {
            return (this.customerStatus ?? '').toLowerCase() === 'cash';
        }
        return true;
    }

    onPaymentClose(): void {
        this.appFacadeService.setPaymentOpen(false);
    }

    toggleOverlay(state: boolean) {
        this.overlayStates = state;
    }

    filterPayableInvoices() {
        /**
         * Toggle Payment button
         */
        if (this.payInvoicesClicked() === false) {
            this.payInvoicesClicked.set(true);
            this.gridColumnApi?.setColumnVisible('selection', true);
        } else {
            this.payInvoicesClicked.set(false);
            this.gridColumnApi?.setColumnVisible('selection', false);
            this.appFacadeService.unsetSelectedInvoices(this.selectedInvoices);
        }
        this.gridApi?.sizeColumnsToFit();
    }

    setResponsiveGrid(width: number) {
        this.gridReady$.next(false);
        if (width > 1024) {
            this.columnDefs = [
                ...(this.isMultiPaymentAvailable()
                    ? [
                          {
                              field: 'selection',
                              headerName: '',
                              minWidth: 50,
                              width: 100,
                              cellClass: 'theme-table-row',
                              hide: !this.payInvoicesClicked(),
                              cellRenderer: InvoiceSelectionCheckboxRendererComponent
                          }
                      ]
                    : []),
                ...(this.featureToggleService.isAvailable('canSeeInvoiceContractIdCol')
                    ? [
                          {
                              field: 'Identifiers.ContractId',
                              headerName: this.translateService.instant('InvoiceColumnHeaderContract#'),
                              cellClass: 'theme-table-row',
                              headerClass: 'theme-table-header',
                              cellRenderer: (params: { value: number }) => `<span class="theme-table-row">${params.value}</span>`
                          }
                      ]
                    : []),
                {
                    field: 'InvoiceNumber',
                    headerName: this.translateService.instant('invoiceNumber'),
                    cellClass: 'theme-table-row',
                    headerClass: 'theme-table-header',
                    sortable: true,
                    cellRenderer: InvoiceNumberCellRendererComponent,
                    onCellClicked: params => {
                        if (params.value && this.appFacadeService.featureToggleService.isAvailable('canSeeInvoiceDetails')) {
                            this.loadInvoiceDetailModal(params.data);
                        }
                    }
                },
                ...(this.featureToggleService.isAvailable('canSeeInvoiceDepotCol')
                    ? [
                          {
                              field: 'DepotName',
                              headerName: this.translateService.instant('Depot'),
                              cellClass: 'theme-table-row',
                              headerClass: 'theme-table-header'
                          }
                      ]
                    : []),

                ...(this.featureToggleService.isAvailable('canSeeInvoiceSiteCol')
                    ? [
                          {
                              field: 'AdditionalFields.Address',
                              headerName: this.translateService.instant('location'),
                              minWidth: 230,
                              wrapText: true,
                              autoHeight: true,
                              cellClass: 'theme-table-row',
                              headerClass: 'theme-table-header',
                              cellRenderer: (params: { value: Address }) => {
                                  return this.formatAddressPipe.format(params?.value);
                              }
                          }
                      ]
                    : []),
                ...(this.featureToggleService.isAvailable('canSeeInvoiceTypeCol')
                    ? [
                          {
                              field: 'Type',
                              headerName: this.translateService.instant('Type'),
                              cellClass: 'theme-table-row',
                              headerClass: 'theme-table-header'
                          }
                      ]
                    : []),
                ...(this.featureToggleService.isAvailable('canSeeInvoiceBilledDateCol')
                    ? [
                          {
                              field: 'InvoiceDate',
                              headerName: this.translateService.instant('InvoiceColumnHeaderBilledDate'),
                              cellClass: 'theme-table-row',
                              cellRenderer: this.formatDate,
                              headerClass: 'theme-table-header'
                          }
                      ]
                    : []),
                ...(this.featureToggleService.isAvailable('canSeeInvoiceDueDateCol')
                    ? [
                          {
                              field: 'DueDate',
                              headerName: this.translateService.instant('InvoiceColumnHeaderDueDate'),
                              cellClass: 'theme-table-row',
                              cellRenderer: this.formatDate,
                              headerClass: 'theme-table-header'
                          }
                      ]
                    : []),
                ...(this.featureToggleService.isAvailable('canSeeInvoicePOCol')
                    ? [
                          {
                              field: 'CustomerPurchaseOrderNumber',
                              headerName: this.translateService.instant('InvoiceColumnHeaderPO'),
                              cellClass: 'theme-table-row',
                              headerClass: 'theme-table-header'
                          }
                      ]
                    : []),
                ...(this.featureToggleService.isAvailable('canSeeInvoiceJobNumberCol')
                    ? [
                          {
                              field: 'CustomerJobNumber',
                              headerName: this.translateService.instant('InvoiceColumnHeaderJobNumber'),
                              cellClass: 'theme-table-row',
                              headerClass: 'theme-table-header'
                          }
                      ]
                    : []),
                {
                    field: 'GrandTotal',
                    headerName: this.translateService.instant('invoiceTotal'),
                    cellClass: 'theme-table-row',
                    headerClass: 'theme-table-header',
                    cellRenderer: AmountRendererComponent,
                    sortable: true
                },
                ...(this.featureToggleService.isAvailable('canSeeInvoiceGrandTaxTotalCol')
                    ? [
                          {
                              field: 'GrandTaxTotal',
                              headerName: this.translateService.instant('invoiceTax'),
                              cellClass: 'theme-table-row',
                              headerClass: 'theme-table-header',
                              cellRenderer: AmountRendererComponent,
                              sortable: true
                          }
                      ]
                    : []),
                ...(this.featureToggleService.isAvailable('canSeeInvoiceAmountDueCol')
                    ? [
                          {
                              field: 'AmountDue',
                              headerName: this.translateService.instant('InvoiceColumnHeaderAmountDue'),
                              cellClass: 'theme-table-row',
                              cellRenderer: AmountRendererComponent,
                              headerClass: 'theme-table-header'
                          }
                      ]
                    : [])
            ];
            this.headerHeight = 35;
            this.isFullWidth = false;
            this.rowHeight = 65;
        }
        if (width < 1024) {
            this.columnDefs = [];
            this.headerHeight = 0;
            this.isFullWidth = true;
            this.rowHeight = this.rowHeight = this.appFacadeService.featureToggleService.isSyrinx() ? 315 : 380;
        }
        this.gridReady$.next(true);
    }

    loadInvoiceDetailModal(data: Invoice) {
        this.appFacadeService.fetchInvoiceDetail(this.customerId, data.Id);
    }

    closeInvoiceDetail() {
        this.appFacadeService.setSubActiveTab(SubComponentsToLoad.Default);
        this.appFacadeService.clearInvoiceDetail();
    }

    viewInvoicePdf(data: Invoice) {
        if (this.appFacadeService.featureToggleService.isAvailable('pdfDownloadFeature')) {
            const organizationType: string = this.appMediatorService.localStorageService?.getCurrentUser?.organizationType as string;
            const contractdetails: GetDocumentParam = {
                customerId: data?.CustomerId ?? '0',
                contractId: data.Id, // InvoiceId == contractId
                orgType: organizationType,
                documentValue: data.Id
            };
            this.appFacadeService.downLoadInvoiceDocument(contractdetails, data.Id);
        }
    }
}
